import {Navigate, Route, Routes, useLocation} from 'react-router'
import {Suspense, lazy, useEffect, type JSX} from 'react'
import {useQueryStringObject} from '../store/routerSelectors'
import useTypedSelector from '../hooks/use-typed-selector'
import {activeLocationSelector} from '../store/state/locations/selectors'
import {useLatestLocationThreatScoresFromWarningAndScoring} from '../store/state/was-warning-and-scoring-adapter'
import {type Location} from '../store/state/locations/state'
import {isUserAllowed} from '../values/Role'
import {rolesSelector} from '../store/state/roles/selectors'
import {currentUserSelector, isInternalUserSelector} from '../store/state/current-user/selectors'
import {fleetConfigSelector} from '../store/state/config/selectors'
import {useFleetConfigKeyQuery} from '../query/fleet-config'
import {FleetConfigKeysType, type FleetConfigStatus} from '../store/state/config/state'
import {isBoolean, isEmpty, isObject, isString} from 'lodash'
import {StringUtils} from '../utils/Utils'
import {isFleetConfigEnabled} from '../api/fleet-config'
import {useHasActiveVessels} from '../hooks/vessels'
import {NavigationBarLayout} from './layout'
import type {Location as RouterLocation} from 'react-router'
import * as Sentry from '@sentry/react'
import {DEFAULT_OTSM_REDIRECT_URL, DEFAULT_REDIRECT_URL} from '../values/default-redirect-url'

const LazyDashboardPage = lazy(() => import('../pages/dashboard-v2/dashboard'))
const LazyEmailRecipientsManagementPage = lazy(
    () => import('../pages/system-page/email-recipients-management-page'),
)
const LazySwaggerLinksPage = lazy(() => import('../pages/swagger-links-page/swagger-links-page'))
const LazyLicensesPage = lazy(() => import('../pages/licenses/licenses-page'))
const LazyUserManagementPage = lazy(() => import('../pages/user-management/user-management'))
const LazyNextShellPage = lazy(() => import('../pages/next-shell/next-shell'))
const LazyLogoutTriggerPage = lazy(() => import('../pages/logout-trigger/logout-trigger'))
const LazyVesselConfigurationPage = lazy(
    () => import('../pages/vessels/configuration/vessel-configuration'),
)
const LazyVesselsPage = lazy(() => import('../pages/vessels/listing/vessels'))
const LazyMyVesselsDeepLinkingPage = lazy(
    () => import('../pages/my-vessels-v2/deep-linking/my-vessels-deep-linking'),
)
const LazyMyVesselsPage = lazy(() => import('../pages/my-vessels-v2/listing/my-vessels'))
const LazyPolicyManagementPage = lazy(() => import('../pages/policy-management/policy-management'))
const LazyVesselManagementPage = lazy(() => import('../pages/vessel-management/vessel-management'))
const LazyIncidentsBetaPage = lazy(() => import('../pages/incidents-v3/incidents'))
const LazyReportsPage = lazy(() => import('../pages/reports-v2/reports'))
const LazyUnknownAssetPage = lazy(() => import('../pages/network-assets/network-assets-page'))
const LazyUsbDevicesPage = lazy(() => import('../pages/usb-devices/usb-devices-page'))
const LazySoftwareInventoryPage = lazy(
    () => import('../pages/software-inventory/software-inventory-page'),
)
const LazyNMEAInventoryPage = lazy(() => import('../pages/nmea/nmea-page'))
const LazyMetricsBetaPage = lazy(() => import('../pages/metrics-beta/metrics-beta-page'))
const LazyLocationOTInventoryPage = lazy(
    () => import('../pages/ot-inventory/location-inventory-page'),
)
const LazyVesselListPage = lazy(() => import('../pages/ot-inventory/vessel-list-page'))
const LazyIncidentsReportPage = lazy(
    () => import('../pages/incidents-report/incidents-report-page'),
)
const LazyOperationalDashboardPage = lazy(
    () => import('../pages/operational-dashboard/operational-dashboard-page'),
)
const LazyNewVesselsPage = lazy(() => import('../pages/my-vessels-v3'))
const LazyEventsPage = lazy(() => import('../pages/events/events-page'))
const LazyAssetDetailsModal = lazy(() => import('../modals/asset-details/asset-details-modal'))

const redirects = [
    {from: '/my-vessels-beta', to: '/my-vessels'},
    {from: '/reports-beta', to: '/reports'},
    {from: '/incidentsbeta', to: '/incidents'},
    {from: '/incidents-beta', to: '/incidents'},
    {from: '/dashboardbeta', to: '/dashboard'},
    {from: '/unknown-assets-beta', to: '/network-assets'},
    {from: '/unknown-assets', to: '/network-assets'},
    {from: '/metrics-beta', to: '/metrics'},
]

const standardRoutes = [
    {path: '/dashboard', element: <LazyDashboardPage />},
    {path: '/my-vessels', element: <LazyMyVesselsPage />},
    {path: '/my-vessels/:location', element: <LazyMyVesselsDeepLinkingPage />},
    {path: '/user-management', element: <LazyUserManagementPage />},
    {path: '/incidents', element: <LazyIncidentsBetaPage />},
    {path: '/events', element: <LazyEventsPage />},
    {path: '/metrics', element: <LazyMetricsBetaPage />},
    {path: '/ot-inventory-vessels', element: <LazyVesselListPage />},
    {path: '/otsm', element: <LazyVesselListPage />},
    {path: '/ot-inventory/:location/assets', element: <LazyLocationOTInventoryPage />},
    {path: '/incidentsReport', element: <LazyIncidentsReportPage />},
    {path: '/operational-dashboard', element: <LazyOperationalDashboardPage />},
    {path: '/new-vessels', element: <LazyNewVesselsPage />},
]

const developerRoutes = [
    {path: '/email-recipients', element: <LazyEmailRecipientsManagementPage />},
    {path: '/swagger-links', element: <LazySwaggerLinksPage />},
    {path: '/customer-packages', element: <LazyLicensesPage />},
    {path: '/vessels/:location', element: <LazyVesselConfigurationPage />},
    {path: '/vessels', element: <LazyVesselsPage />},
]

const policyManagementRoutes = [
    {path: '/policy-management', element: <LazyPolicyManagementPage />},
    {path: '/vessel-management', element: <LazyVesselManagementPage />},
]

const getFleetConfigRoutes = (isInternalUser: boolean, fleetConfig: FleetConfigStatus) => [
    {
        path: '/reports',
        element: <LazyReportsPage />,
        condition: isInternalUser || fleetConfig.reports,
    },
    {
        path: '/network-assets',
        element: <LazyUnknownAssetPage />,
        condition: isInternalUser || fleetConfig.networkAssets,
    },
    {
        path: '/usb-devices',
        element: <LazyUsbDevicesPage />,
        condition: isInternalUser || fleetConfig.usbInventory,
    },
    {
        path: '/software',
        element: <LazySoftwareInventoryPage />,
        condition: isInternalUser || fleetConfig.softwareInventory,
    },
    {
        path: '/nmea',
        element: <LazyNMEAInventoryPage />,
        condition: isInternalUser || fleetConfig.nmeaInventory,
    },
]

function useAuthPermissions() {
    const userRoles = useTypedSelector(rolesSelector)
    const isInternalUser = useTypedSelector(isInternalUserSelector)
    const isUserRoleAllowed = isUserAllowed(userRoles, ['DEVELOPER', 'THREAT_ANALYST'])
    const hasDeveloperAccess = isInternalUser && isUserRoleAllowed
    const hasPolicyManagementAccess = isUserAllowed(userRoles, [
        'DEVELOPER',
        'THREAT_ANALYST',
        'CUSTOMER_ADMIN',
    ])
    const user = useTypedSelector(currentUserSelector)

    return {user, isInternalUser, hasDeveloperAccess, hasPolicyManagementAccess}
}

function handleRedirects(pathname: string, location: RouterLocation) {
    // Check for asset management expansion
    if (location.search.match('assetManagementExpanded=true')) {
        return '/network-assets'
    }

    // Check configured redirects
    for (const redirect of redirects) {
        if (pathname.startsWith(redirect.from)) {
            return redirect.to
        }
    }

    return null
}

const DISALLOWED_REDIRECT_PATHS = new Set(['/logout-trigger'])
function getRedirectUrl(state: unknown): {
    url: string
    // Whether `url` is a url that came from previous navigation
    hasPreviousUrl: boolean
} {
    const url = DEFAULT_REDIRECT_URL
    const hasPreviousUrl = false

    const redirect = {url, hasPreviousUrl}

    if (!isObject(state) || !('from' in state) || !isObject(state.from)) {
        return redirect
    }

    const from = state.from

    if (
        'pathname' in from &&
        StringUtils.validString(from.pathname) &&
        !DISALLOWED_REDIRECT_PATHS.has(from.pathname)
    ) {
        redirect.url = from.pathname
        redirect.hasPreviousUrl = true

        if ('search' in from && StringUtils.validString(from.search)) {
            redirect.url = `${redirect.url}/${from.search}`
        }
    }

    return redirect
}

// `/next/*` is served by the persistent <NextShell> overlay (mounted once below,
// shown only on `/next/*`), not by a routed element. This empty match simply
// keeps `/next/*` URLs from falling through to the catch-all redirect inside
// NavigationBarLayout.
function NextShellRouteSlot() {
    return null
}

function legacyUpdateToWorkWithV1Layout(activeLocation: Location) {
    const root = document.getElementById('root')
    if (root) {
        if (activeLocation.code) {
            root.className = 'root--selected-location'
        } else if (activeLocation.location === 'fleet-view') {
            root.className = ''
        }
    }
}

export function AuthenticatedRoutes(): JSX.Element {
    const queryString = useQueryStringObject()

    const displayAssetDetailsModal = queryString['assetDetails']

    const location = useLocation()

    legacyUpdateToWorkWithV1Layout(useTypedSelector(activeLocationSelector))
    const {user, isInternalUser, hasDeveloperAccess, hasPolicyManagementAccess} =
        useAuthPermissions()

    // CDS-6008: replaces the WebSocket registration that used to run for the
    // whole session from fetchInitialData() and reached InfluxDB through
    // basal-ganglia. Mounted here so the per-vessel scores stay available
    // everywhere they were before.
    useLatestLocationThreatScoresFromWarningAndScoring()

    useEffect(() => {
        const validUser = isString(user.user) && !isEmpty(user.user) && isBoolean(user.external)

        if (validUser) {
            Sentry.setUser({id: user.user, ip_address: '{{ auto }}'})
            Sentry.setTag('external_user', user.external)
        }
    }, [user])

    const fleetConfig = useTypedSelector(fleetConfigSelector)

    const {data: otsmConfig} = useFleetConfigKeyQuery(FleetConfigKeysType.LICENSE_OTSM, {
        // We use suspense here for 2 reason.
        // first so that the data is there straight away
        // when this component is ready to render, thus the condition `isOtsmOnly` below can
        // be evaluated with value from the server straight away, and this will make the
        // redirect works.
        // 2nd, it has the side effects of any child component that needs this data to also
        // see the value from the server, thus it can render what it needs correctly straight away.
        // IE, the nav bar now doesn't flicker between showing full sets of navigation VS only OTSM
        // when the environment is OTSM only.
        suspense: true,
        useErrorBoundary: false,
    })

    const isOtsmOnly = isFleetConfigEnabled(otsmConfig)
    const hasActiveVessel = useHasActiveVessels({suspense: true})
    const shouldRedirectToOtsmLanding = isOtsmOnly && !hasActiveVessel

    const {url, hasPreviousUrl} = getRedirectUrl(location.state)

    let defaultRedirectUrl = url

    if (shouldRedirectToOtsmLanding && !hasPreviousUrl) {
        defaultRedirectUrl = DEFAULT_OTSM_REDIRECT_URL
    }

    const fleetConfigRoutes = getFleetConfigRoutes(isInternalUser, fleetConfig)

    const redirectTo = handleRedirects(location.pathname, location)

    return (
        <>
            {redirectTo ? (
                <Navigate to={redirectTo} />
            ) : (
                <Routes>
                    <Route path="/next/*" element={<NextShellRouteSlot />} />
                    <Route path="/logout-trigger" element={<LazyLogoutTriggerPage />} />
                    <Route element={<NavigationBarLayout />}>
                        {standardRoutes.map((route) => (
                            <Route key={route.path} path={route.path} element={route.element} />
                        ))}

                        {hasDeveloperAccess &&
                            developerRoutes.map((route) => (
                                <Route key={route.path} path={route.path} element={route.element} />
                            ))}

                        {hasPolicyManagementAccess &&
                            policyManagementRoutes.map((route) => (
                                <Route key={route.path} path={route.path} element={route.element} />
                            ))}

                        {fleetConfigRoutes.map(
                            (route) =>
                                route.condition && (
                                    <Route
                                        key={route.path}
                                        path={route.path}
                                        element={route.element}
                                    />
                                ),
                        )}

                        <Route path="*" element={<Navigate to={defaultRedirectUrl} />} />
                    </Route>
                </Routes>
            )}
            {/* Persistent embedded new app (medulla-next). Mounted once when the
                user is authenticated and kept alive for the whole session — it is
                NOT a route, so switching between old and new pages never tears it
                down. It shows itself only on `/next/*` and pre-boots in the
                background so the first new-app view is instant. */}
            <Suspense fallback={null}>
                <LazyNextShellPage />
            </Suspense>
            {displayAssetDetailsModal && (
                <Suspense fallback={<div />}>
                    <LazyAssetDetailsModal />
                </Suspense>
            )}
        </>
    )
}
